import { useState, useEffect, useCallback } from 'react';
import type { CSSProperties } from 'react';
import { Link } from 'react-router-dom';
import {
  Table,
  Input,
  Button,
  Form,
  Space,
  message,
  Select,
  Popover,
  Popconfirm,
  Modal,
  Spin,
  Tooltip,
  Tree,
  Progress,
  Checkbox,
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import {
  MoreOutlined,
  CopyOutlined,
  QuestionCircleOutlined,
  DownloadOutlined,
  FileTextOutlined,
  CodeOutlined,
  StopOutlined,
  UnlockOutlined,
  RightOutlined,
} from '@ant-design/icons';
import { ResizableTitle, MiddleEllipsis, useResizableColumns } from '@components/ResizableTable';
import { renderAlgorithmVersionCell } from '@components/AlgorithmVersionCell';
import { useVersionTrace } from '@components/VersionTraceModal/useVersionTrace';
import { JobStatusTag } from '@components/StatusTag';
import CreateSceneQcJobModal from '@components/CreateSceneQcJobModal';
import TimeRangeChip, {
  resolvePartitionRange,
  type TimeRangeValue,
} from '@components/TimeRangeChip';
import { useCurrentUser } from '@/common/auth/adminAcl';
import { useEnterToSearch } from '@/common/keyboard';
import { completionPercent } from '@/common/progress';
import {
  getSceneQcJobs,
  isolateJob,
  unisolateJob,
  deleteJob,
  getPriorityList,
  getStatusList,
  getJobSourceList,
  getJobLogs,
  getJobBuildLogs,
  getJobBazelBuildLog,
  getSceneTree,
  getBazelWorkflow,
  JobItem,
  JobListParams,
  PriorityOption,
  StatusOption,
  SceneTreeNode,
} from '@apis/simulation';

// ── 运行结果列：成功 / 用户失败 / 仿真失败 / 终止 ──────────────────────────────
// 口径（与后端 job_service._task_result_breakdown 对齐）：
//   成功   = task 通过(succeeded) + 不通过(notPass)（仿真本身跑完）
//   用户失败 = coredump
//   仿真失败 = 执行异常(failed) + 系统异常(unknown)
//   终止   = 取消(cancelled)
// 中间态（未开始/排队/执行中）不计入，故四者之和可能 < 总数。
const RUN_RESULT_SEGMENTS: {
  key: keyof Pick<JobItem, 'resultSuccessNum' | 'resultUserFailNum' | 'resultSimFailNum' | 'resultAbortedNum'>;
  label: string;
  /** value>0 时的文字色 */
  color: string;
  /** value>0 时是否给数字加浅色底片（失败类需要醒目） */
  tint?: string;
}[] = [
  { key: 'resultSuccessNum', label: '成功', color: '#16a34a' },
  { key: 'resultUserFailNum', label: '用户失败', color: '#d97706', tint: '#fdf0e1' },
  { key: 'resultSimFailNum', label: '仿真失败', color: '#dc2626', tint: '#fdecec' },
  { key: 'resultAbortedNum', label: '终止', color: '#8c9099' },
];

// 表头与单元格共用同一套 4 列栅格 → 数字精确落在标签正下方，整列纵向对齐。
// 「成功」列额外带「/总数」，故给更宽的份额。
const RUN_RESULT_GRID: CSSProperties = {
  display: 'grid',
  gridTemplateColumns: '1.7fr 1fr 1fr 1fr',
  alignItems: 'center',
  width: '100%',
};

function RunResultHeader() {
  return (
    <div style={{ lineHeight: 1.4 }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 4 }}>
        运行结果
        <Tooltip
          title={
            <div style={{ fontSize: 12, lineHeight: 1.7 }}>
              <div>成功 = 通过 + 不通过（仿真跑完）</div>
              <div>用户失败 = coredump</div>
              <div>仿真失败 = 执行异常 + 系统异常</div>
              <div>终止 = 取消</div>
            </div>
          }
        >
          <QuestionCircleOutlined style={{ color: '#9b9da3', fontSize: 12, cursor: 'help' }} />
        </Tooltip>
      </div>
      <div style={{ ...RUN_RESULT_GRID, fontSize: 11, fontWeight: 400, color: '#9b9da3', marginTop: 3 }}>
        {RUN_RESULT_SEGMENTS.map((s) => (
          <span key={s.key} style={{ textAlign: 'center' }}>{s.label}</span>
        ))}
      </div>
    </div>
  );
}

function RunResultCell({ record }: { record: JobItem }) {
  const { resultSuccessNum, resultUserFailNum, resultSimFailNum, resultAbortedNum } = record;
  // 后端未回填（任一为 null/undefined）→ 无运行结果数据
  const hasData = [resultSuccessNum, resultUserFailNum, resultSimFailNum, resultAbortedNum]
    .every((v) => typeof v === 'number');
  if (!hasData) {
    return <span style={{ color: '#c4c6cc' }}>-</span>;
  }
  const total = record.totalTaskNum ?? 0;
  return (
    <div style={{ ...RUN_RESULT_GRID, fontVariantNumeric: 'tabular-nums' }}>
      {RUN_RESULT_SEGMENTS.map((seg) => {
        const value = (record[seg.key] as number) ?? 0;
        const isZero = value === 0;
        const isSuccess = seg.key === 'resultSuccessNum';
        const showTint = !isZero && !!seg.tint;
        return (
          <div key={seg.key} style={{ textAlign: 'center' }}>
            <Tooltip title={isSuccess ? `${seg.label} ${value}/${total}（总数）` : seg.label}>
              <span
                style={{
                  display: 'inline-block',
                  minWidth: 22,
                  fontSize: 13,
                  fontWeight: isZero ? 400 : 600,
                  color: isZero ? '#d0d3d9' : seg.color,
                  background: showTint ? seg.tint : 'transparent',
                  borderRadius: 6,
                  padding: showTint ? '1px 8px' : '1px 0',
                  lineHeight: '18px',
                }}
              >
                {value}
                {/* 成功列带「/总数」，总数弱化为背景灰、字号更小，给「N 选自总量」的语境 */}
                {isSuccess && total > 0 && (
                  <span style={{ color: '#c4c6cc', fontWeight: 400, fontSize: 10 }}>/{total}</span>
                )}
              </span>
            </Tooltip>
          </div>
        );
      })}
    </div>
  );
}

// 「只看自己」开关的浏览器记忆 key（'1'=勾选 / '0'=不勾选；无记录默认勾选）
const ONLY_MINE_STORAGE_KEY = 'joblist:onlyMine';

interface JobListQueryParams {
  task_type_id?: string;
  task_id?: string;
  job_id?: number;
  job_name?: string;
  priority?: string | number;
  start_time?: string;
  start_time_start?: string;
  start_time_end?: string;
  task_status?: string;
  status?: string | number;
  creator?: string;
  /** 数据源（JobSourceEnum.id）：1=日常仿真 2=daily 仿真 3=cicd 仿真 */
  source?: string | number;
  /**
   * partition_time 范围过滤(左闭右开)。冷热分离 v2 一期:存带 mode 的标签联合
   * 而非解析后的字符串,把 preset 的求值推迟到发请求那一刻(loadJobList),
   * 避免 end 冻结在挂载时刻导致新建任务查不到。前端时间选择器统一走
   * partition_time,让后端触发 PG 分区 pruning,避免扫全部子分区触发 timeout。
   */
  partition_time_range?: TimeRangeValue | null;
  page?: number;
  per_page?: number;
}

export default function SceneQcListPage() {
  // 场景质检子页面固定 SCENE_QC 任务类型，无需 URL 参数
  const { onTrace, modal: versionTraceModal } = useVersionTrace();
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState<JobItem[]>([]);
  const [total, setTotal] = useState(0);
  const [priorityList, setPriorityList] = useState<PriorityOption[]>([]);
  const [statusList, setStatusList] = useState<StatusOption[]>([]);
  const [sourceList, setSourceList] = useState<StatusOption[]>([]);
  const [queryParams, setQueryParams] = useState<JobListQueryParams>(() => {
    // 冷热分离 v2 一期:默认最近 30 天,让后端 PG 分区 pruning 覆盖热路径。
    // 存 preset 标签联合而非解析后字符串,发请求(loadJobList)时才现算,
    // end 跟随现在,避免冻结导致新任务查不到。
    return {
      // 本页固定 SCENE_QC，走 /scene-qc/jobs 接口反查（不再靠 task_type_id URL 段）
      page: 1,
      per_page: 20,
      partition_time_range: { mode: 'preset', days: 30 },
    };
  });
  // 当前登录用户：用于把「创建人」筛选默认填为当前用户 email（与后端写入的 creator 对齐）
  const { me, loading: userLoading } = useCurrentUser();
  const currentUserEmail = me?.user?.email || '';
  // 「只看自己」开关：浏览器记忆（localStorage），每次进入页面读取。
  // 勾选 → 创建人筛选自动带上当前用户；不勾选 → 不加创建人筛选。
  // 历史默认行为是进入即按自己过滤，故无记录时默认勾选，保持向后兼容。
  const [onlyMine, setOnlyMine] = useState<boolean>(() => {
    const v = localStorage.getItem(ONLY_MINE_STORAGE_KEY);
    return v === null ? true : v === '1';
  });
  // creator 默认值是否已注入（避免重复设置 + 首次请求等待用户信息就绪）
  const [creatorInitialized, setCreatorInitialized] = useState(false);
  const [logModalVisible, setLogModalVisible] = useState(false);
  const [logContent, setLogContent] = useState<string>('');
  const [logLoading, setLogLoading] = useState(false);
  // 编译日志的完整日志下载链接（仅 logKind='build' 时可能有值，用于「查看完整日志」入口）
  const [logDownloadUrl, setLogDownloadUrl] = useState<string | null>(null);
  // 日志类型：'run' = Job 运行日志；'build' = 编译任务的编译日志（决定弹窗标题与数据源）
  const [logKind, setLogKind] = useState<'run' | 'build'>('run');
  const [currentJobId, setCurrentJobId] = useState<number | null>(null);
  const [createModalVisible, setCreateModalVisible] = useState(false);
  // 复制 job：打开弹窗时携带原 record（弹窗内部回填发起参数）；null 表示全新创建
  const [copyRecord, setCopyRecord] = useState<JobItem | null>(null);
  // 场景树：仅供「场景集合」列回填历史 Job 绑定的路径展示（创建弹窗自行加载自己的树）
  const [sceneTree, setSceneTree] = useState<SceneTreeNode[]>([]);

  // 加载优先级列表
  const loadPriorityList = useCallback(async () => {
    try {
      const list = await getPriorityList();
      setPriorityList(list);
    } catch (err) {
      console.error('加载优先级列表失败:', err);
    }
  }, []);

  // 加载状态列表
  const loadStatusList = useCallback(async () => {
    try {
      const list = await getStatusList();
      setStatusList(list);
    } catch (err) {
      console.error('加载状态列表失败:', err);
    }
  }, []);

  // 加载数据源（JobSourceEnum）下拉列表
  const loadSourceList = useCallback(async () => {
    try {
      const list = await getJobSourceList();
      setSourceList(list);
    } catch (err) {
      console.error('加载数据源列表失败:', err);
    }
  }, []);

  // 加载任务列表
  const loadJobList = useCallback(async () => {
    setLoading(true);
    try {
      // 冷热分离 v2 一期:在发请求这一刻才解析时间窗。preset 现算 [now-N天, now],
      // end 跟随现在,故「提交后 onSuccess 自动刷新 / 手动查询」都能查到刚创建的
      // 任务(partition_time = 创建时刻);absolute 原样;null = 不过滤(后端兜底)。
      const resolvedRange = resolvePartitionRange(queryParams.partition_time_range ?? null);
      const params: JobListParams = {
        jobId: queryParams.job_id,
        jobName: queryParams.job_name,
        priority: queryParams.priority,
        status: queryParams.status,
        creator: queryParams.creator || undefined,
        source: queryParams.source,
        pageSize: queryParams.per_page,
        current: queryParams.page,
        // 冷热分离 v2 一期:时间范围走 partition_time,触发 PG 分区 pruning
        partitionTimeStart: resolvedRange?.start,
        partitionTimeEnd: resolvedRange?.end,
      };

      const response = await getSceneQcJobs(params);
      setData(response.tasks);
      setTotal(response.total);
    } catch (err) {
      console.error('加载任务列表失败:', err);
    } finally {
      setLoading(false);
    }
  }, [queryParams]);

  // 加载场景树：拉全量（含 isHidden=true）作为单一数据源。
  // 两个用途分别处理：
  // - 「场景集合」列回填历史 Job 绑定的路径：直接用全量树，否则隐藏集合下的
  //   id 找不到 path 就会显示「-」，体验不一致。
  // - 创建 Job 的 TreeSelect：在 convertSceneTreeToTreeData 里 prune 掉
  //   isHidden=true 的子树，与「场景管理」前台权限一致——隐藏集合不可被新引用。
  const loadSceneTree = useCallback(async () => {
    try {
      const tree = await getSceneTree(true);
      setSceneTree(tree);
    } catch (err) {
      console.error('加载场景树失败:', err);
    }
  }, []);

  // 初始化加载优先级、状态、数据源列表
  useEffect(() => {
    loadPriorityList();
    loadStatusList();
    loadSourceList();
    loadSceneTree(); // 加载场景树用于显示
  }, [loadPriorityList, loadStatusList, loadSourceList, loadSceneTree]);

  // 在场景树中查找节点并构建完整路径
  const findScenePath = (nodes: SceneTreeNode[], targetId: number, path: string[] = []): string[] | null => {
    for (const node of nodes) {
      const currentPath = [...path, node.name];
      if (node.id === targetId) {
        return currentPath;
      }
      if (node.children && node.children.length > 0) {
        const found = findScenePath(node.children, targetId, currentPath);
        if (found) {
          return found;
        }
      }
    }
    return null;
  };

  // 在场景树中查找节点并返回节点信息
  const findSceneNode = (nodes: SceneTreeNode[], targetId: number): SceneTreeNode | null => {
    for (const node of nodes) {
      if (node.id === targetId) {
        return node;
      }
      if (node.children && node.children.length > 0) {
        const found = findSceneNode(node.children, targetId);
        if (found) {
          return found;
        }
      }
    }
    return null;
  };


  // 检查节点或其子节点是否在选中列表中
  const hasSelectedDescendant = (node: SceneTreeNode, selectedIds: number[]): boolean => {
    if (selectedIds.includes(node.id)) {
      return true;
    }
    if (node.children && node.children.length > 0) {
      return node.children.some(child => hasSelectedDescendant(child, selectedIds));
    }
    return false;
  };

  // 递归转换单个节点为Tree格式（处理多层嵌套）
  const convertNodeToTreeData = (node: SceneTreeNode, selectedIds: number[]): any => {
    const isSelected = selectedIds.includes(node.id);
    
    // 如果节点被选中且是父节点（nodeFlag=0），显示所有子节点
    // 否则，只显示包含选中节点的子节点
    let children: any[] | undefined = undefined;
    if (node.children && node.children.length > 0) {
      if (isSelected && node.nodeFlag === 0) {
        // 父节点被选中，递归显示所有子节点（包括多层嵌套）
        children = node.children.map(child => convertNodeToTreeData(child, selectedIds));
      } else {
        // 父节点未被选中，只显示包含选中节点的子节点路径
        const filteredChildren = node.children
          .filter(child => hasSelectedDescendant(child, selectedIds))
          .map(child => convertNodeToTreeData(child, selectedIds));
        children = filteredChildren.length > 0 ? filteredChildren : undefined;
      }
    }
    
    return {
      title: node.name,
      key: node.id,
      isLeaf: node.nodeFlag === 1,
      children: children,
      ...(isSelected && { className: 'selected-scene-node' }),
    };
  };

  // 将场景树转换为Tree组件格式，只显示包含选中节点的路径
  const convertSceneTreeToTreeForDisplay = (nodes: SceneTreeNode[], selectedIds: number[]): any[] => {
    return nodes
      .filter(node => hasSelectedDescendant(node, selectedIds)) // 只保留包含选中节点的分支
      .map(node => convertNodeToTreeData(node, selectedIds));
  };

  // 统计treeData中的叶子节点个数
  const countLeafNodes = (treeData: any[]): number => {
    let count = 0;
    for (const node of treeData) {
      if (node.isLeaf === true) {
        count++;
      }
      if (node.children && node.children.length > 0) {
        count += countLeafNodes(node.children);
      }
    }
    return count;
  };

  // 用户信息加载完后，把当前用户 email 注入「创建人」筛选 + 表单
  // 仅在首次进入页面时生效；用户中途清空/改写筛选后不再覆盖
  useEffect(() => {
    if (creatorInitialized || userLoading) return;
    // 仅当「只看自己」处于勾选态时，才默认按当前用户过滤
    if (onlyMine && currentUserEmail) {
      form.setFieldsValue({ creator: currentUserEmail });
      setQueryParams(prev => ({ ...prev, creator: currentUserEmail, page: 1 }));
    }
    // 冷热分离 v2 一期:首次 mount 把默认最近 30 天 preset 塞进 TimeRangeChip,
    // 与 queryParams 的初始 partition_time_range 保持一致。
    form.setFieldsValue({ partition_time_range: { mode: 'preset', days: 30 } });
    setCreatorInitialized(true);
  }, [userLoading, currentUserEmail, creatorInitialized, onlyMine, form]);

  // 首次拉列表必须等 creator 默认值就绪，避免「先发空查询、再发带 creator 的查询」两次请求
  useEffect(() => {
    if (!creatorInitialized) return;
    loadJobList();
  }, [loadJobList, queryParams, creatorInitialized]);

  // 查询
  const handleSearch = () => {
    const values = form.getFieldsValue();
    const newParams: JobListQueryParams = {
      ...queryParams,
      job_id: values.job_id ? Number(values.job_id) : undefined,
      job_name: values.job_name || undefined,
      priority: values.priority !== undefined && values.priority !== '' ? values.priority : undefined,
      status: values.task_status !== undefined && values.task_status !== '' ? values.task_status : undefined,
      creator: values.creator?.trim() || undefined,
      source: values.source !== undefined && values.source !== '' ? values.source : undefined,
      page: 1,
    };

    // 处理时间范围
    if (values.start_time && values.start_time.length === 2) {
      newParams.start_time_start = values.start_time[0].format('YYYY-MM-DD HH:mm:ss');
      newParams.start_time_end = values.start_time[1].format('YYYY-MM-DD HH:mm:ss');
    }

    // 冷热分离 v2 一期:处理创建时间范围(partition_time)。只存 TimeRangeValue,
    // preset 的求值推迟到 loadJobList 发请求那一刻(end 跟随现在,故提交后刷新/
    // 手动查询都能查到刚创建的任务);absolute 原样;清空 = null(不过滤,后端兜底
    // 走默认 30 天,与首屏一致)。
    const rangeValue: TimeRangeValue | undefined = values.partition_time_range;
    newParams.partition_time_range = rangeValue ?? null;

    setQueryParams(newParams);
  };

  const enterToSearch = useEnterToSearch(handleSearch);

  // 「只看自己」切换：写入 localStorage 记忆，并立即按新状态刷新创建人筛选。
  const handleToggleOnlyMine = (checked: boolean) => {
    setOnlyMine(checked);
    try {
      localStorage.setItem(ONLY_MINE_STORAGE_KEY, checked ? '1' : '0');
    } catch (err) {
      // 隐私模式 / 配额满时 setItem 可能抛错：记忆失效但不影响本次筛选
      console.warn('保存「只看自己」状态失败:', err);
    }
    const nextCreator = checked ? (currentUserEmail || undefined) : undefined;
    form.setFieldsValue({ creator: checked ? (currentUserEmail || '') : '' });
    setQueryParams(prev => ({ ...prev, creator: nextCreator, page: 1 }));
  };

  // 重置：创建人是否预填取决于「只看自己」当前状态（与首次进入页面一致）
  const handleReset = () => {
    form.resetFields();
    const nextCreator = onlyMine ? (currentUserEmail || undefined) : undefined;
    if (nextCreator) {
      form.setFieldsValue({ creator: nextCreator });
    }
    // 冷热分离 v2 一期:reset 后恢复默认最近 30 天,与首屏一致
    form.setFieldsValue({ partition_time_range: { mode: 'preset', days: 30 } });
    setQueryParams({
      // 本页固定 SCENE_QC，reset 也不带 task_type_id
      page: 1,
      per_page: queryParams.per_page || 20,
      creator: nextCreator,
      partition_time_range: { mode: 'preset', days: 30 },
    });
  };

  // 复制任务：携带原 record 打开创建弹窗（弹窗内部按 record 回填发起参数）
  const handleCopyJob = (record: JobItem) => {
    setCopyRecord(record);
    setCreateModalVisible(true);
  };

  // 打开创建任务弹窗（全新创建）
  const handleCreateTask = () => {
    setCopyRecord(null);
    setCreateModalVisible(true);
  };


  // 隔离任务：调度器保留任务现场与日志，可解除
  const handleIsolate = async (jobId: number) => {
    try {
      await isolateJob(jobId);
      message.success('Job 已隔离');
      loadJobList();
    } catch (err) {
      console.error('任务隔离失败:', err);
    }
  };

  // 解除任务隔离
  const handleUnisolate = async (jobId: number) => {
    try {
      await unisolateJob(jobId);
      message.success('已解除隔离');
      loadJobList();
    } catch (err) {
      console.error('解除隔离失败:', err);
    }
  };

  // 取消任务
  const handleDelete = async (jobId: number) => {
    try {
      await deleteJob(jobId);
      message.success('Job 取消成功');
      loadJobList();
    } catch (err) {
      console.error('任务取消失败:', err);
    }
  };
  // 注：单 Job 行内"刷新状态"按钮已下线，统一在「系统 → 系统参数运维 →
  // 运维操作 · 批量刷新未完成 Job 状态」入口触发，避免单条点点点骚扰运维。

  // 查看日志
  const handleViewLog = async (jobId: number) => {
    setCurrentJobId(jobId);
    setLogKind('run');
    setLogModalVisible(true);
    setLogContent('');
    setLogDownloadUrl(null);
    setLogLoading(true);

    try {
      const logs = await getJobLogs(jobId);
      setLogContent(logs);
    } catch (err) {
      console.error('加载日志失败:', err);
      setLogContent('加载日志失败');
    } finally {
      setLogLoading(false);
    }
  };

  // 查看编译日志（仅编译任务）
  const handleViewBuildLog = async (jobId: number) => {
    setCurrentJobId(jobId);
    setLogKind('build');
    setLogModalVisible(true);
    setLogContent('');
    setLogDownloadUrl(null);
    setLogLoading(true);

    try {
      const { logContent, logDownloadUrl } = await getJobBuildLogs(jobId);
      setLogContent(logContent);
      setLogDownloadUrl(logDownloadUrl ?? null);
    } catch (err) {
      console.error('加载编译日志失败:', err);
      setLogContent('加载编译日志失败');
    } finally {
      setLogLoading(false);
    }
  };

  // 查看 bazel 编译日志（仅 package_bazel 任务）：链接来自编译平台回调
  // ``bazel_build_result.call_back.buildLog``，新标签直接打开；找不到/未回填时
  // 提示「请联系管理员查看」。
  const handleViewBazelBuildLog = async (jobId: number) => {
    try {
      const url = await getJobBazelBuildLog(jobId);
      if (!url) {
        message.warning('未找到编译日志，请联系管理员查看');
        return;
      }
      window.open(url, '_blank', 'noopener,noreferrer');
    } catch (err) {
      console.error('查询编译日志失败:', err);
      message.error('查询编译日志失败，请联系管理员查看');
    }
  };

  // 查看 Argo Workflow（仅 bazel 编译 job）：取最近一次编译触发记录的 workflowUrl 后新开页
  const handleViewArgoWorkflow = async (jobId: number) => {
    try {
      const info = await getBazelWorkflow(jobId);
      if (info?.workflowUrl) {
        window.open(info.workflowUrl, '_blank', 'noopener,noreferrer');
      } else {
        message.warning('该 Job 暂无 Argo Workflow 链接');
      }
    } catch (err) {
      console.error('获取 Argo Workflow 失败:', err);
    }
  };

  const { columnWidths, resizeHeaderCell } = useResizableColumns({
    jobId: 80,
    jobName: 200,
    taskTypeName: 110,
    sourceName: 90,
    algorithmVersion: 120,
    sceneSetIds: 100,
    createTime: 160,
    endTime: 160,
    creator: 90,
    progress: 88,
    runResult: 244,
    statusName: 100,
    action: 180,
  });

  // 表格列定义
  const columns: ColumnsType<JobItem> = [
    {
      title: 'JobID',
      dataIndex: 'jobId',
      key: 'jobId',
      width: columnWidths.jobId,
      align: 'center',
      onHeaderCell: resizeHeaderCell('jobId'),
      render: (text) => text || '-',
    },
    {
      title: 'Job 名称',
      dataIndex: 'jobName',
      key: 'jobName',
      width: columnWidths.jobName,
      align: 'center',
      onHeaderCell: resizeHeaderCell('jobName'),
      render: (text, record) => {
        const display = text || '-';
        return (
          <Link
            to={`/job-tasks/${record.jobId}`}
            style={{ color: '#5e6ad2', cursor: 'pointer', display: 'block' }}
          >
            <MiddleEllipsis text={display} />
          </Link>
        );
      },
    },
    {
      title: '任务类型',
      dataIndex: 'taskTypeName',
      key: 'taskTypeName',
      width: columnWidths.taskTypeName,
      align: 'center',
      onHeaderCell: resizeHeaderCell('taskTypeName'),
      render: (text) => text || '-',
    },
    {
      title: '算法版本',
      dataIndex: 'algorithmVersion',
      key: 'algorithmVersion',
      width: columnWidths.algorithmVersion,
      align: 'center',
      onHeaderCell: resizeHeaderCell('algorithmVersion'),
      render: (_text, record) => renderAlgorithmVersionCell(record, onTrace),
    },
    {
      title: '场景集合',
      dataIndex: 'sceneSetIds',
      key: 'sceneSetIds',
      width: columnWidths.sceneSetIds,
      align: 'center',
      onHeaderCell: resizeHeaderCell('sceneSetIds'),
      render: (text, record) => {
        // 按场景发起：sceneSetIds 存的是场景 id 串（非场景集树节点），不走树解析，直接展示数量
        if (record.sceneSource === 'scene') {
          const sceneCount = (text || '')
            .split(',')
            .map((id: string) => id.trim())
            .filter(Boolean).length;
          return sceneCount > 0 ? `${sceneCount}个场景` : '-';
        }

        if (!text || !text.trim() || sceneTree.length === 0) {
          return '-';
        }

        const ids = text.split(',').map((id: string) => parseInt(id.trim())).filter((id: number) => !isNaN(id));
        const foundIds = ids.filter((id: number) => {
          const path = findScenePath(sceneTree, id);
          return path !== null;
        });

        if (foundIds.length === 0) {
          return '-';
        }

        const treeData = convertSceneTreeToTreeForDisplay(sceneTree, foundIds);
        const leafNodeCount = countLeafNodes(treeData);
        const displayText = leafNodeCount > 0 
          ? `${leafNodeCount}个场景集` 
          : '-';

        return (
          <Popover
            title="场景集合"
            content={
              <div style={{ maxHeight: 400, overflow: 'auto', width: 300 }}>
                <Tree
                  treeData={treeData}
                  defaultExpandAll
                  selectedKeys={foundIds.map((id: number) => id.toString())}
                  checkable={false}
                  showLine={{ showLeafIcon: false }}
                />
              </div>
            }
            trigger="click"
            placement="right"
          >
            <a style={{ color: '#5e6ad2', cursor: 'pointer' }}>
              {displayText}
            </a>
          </Popover>
        );
      },
    },
    {
      title: '创建时间',
      dataIndex: 'createTime',
      key: 'createTime',
      width: columnWidths.createTime,
      align: 'center',
      onHeaderCell: resizeHeaderCell('createTime'),
      render: (text) => {
        if (!text) return '-';
        try {
          const date = new Date(text);
          const formatted = date.toLocaleString('zh-CN');
          return <MiddleEllipsis text={formatted} />;
        } catch {
          return text;
        }
      },
    },
    {
      title: '结束时间',
      dataIndex: 'endTime',
      key: 'endTime',
      width: columnWidths.endTime,
      align: 'center',
      onHeaderCell: resizeHeaderCell('endTime'),
      render: (text) => {
        if (!text) return '-';
        try {
          const date = new Date(text);
          const formatted = date.toLocaleString('zh-CN');
          return <MiddleEllipsis text={formatted} />;
        } catch {
          return text;
        }
      },
    },
    {
      title: '数据源',
      dataIndex: 'sourceName',
      key: 'sourceName',
      width: columnWidths.sourceName,
      align: 'center',
      onHeaderCell: resizeHeaderCell('sourceName'),
      render: (text) => text || '-',
    },
    {
      title: '创建人',
      dataIndex: 'creator',
      key: 'creator',
      width: columnWidths.creator,
      align: 'center',
      onHeaderCell: resizeHeaderCell('creator'),
      render: (text) => {
        if (!text) return '-';
        return <MiddleEllipsis text={text} />;
      },
    },
    {
      title: '进度',
      key: 'progress',
      width: columnWidths.progress,
      align: 'center',
      onHeaderCell: resizeHeaderCell('progress'),
      render: (_, record) => {
        const { totalTaskNum, successTaskNum, failTaskNum } = record;
        if (totalTaskNum === 0 || totalTaskNum === undefined) {
          return <span style={{ color: '#c4c6cc' }}>-</span>;
        }
        const completedNum = successTaskNum + failTaskNum;
        const percent = completionPercent(completedNum, totalTaskNum);
        const done = percent >= 100;
        // 进度只表达「完成度」，颜色单一不刺眼：完成=绿、进行中=品牌蓝；
        // 通过/失败的归因交给右侧「运行结果」列，避免这里再用红环误导。
        const strokeColor = done ? '#52c41a' : '#5e6ad2';

        return (
          <Tooltip title={`已完成 ${completedNum}/${totalTaskNum} · 成功 ${successTaskNum} / 失败 ${failTaskNum}`}>
            <div style={{ display: 'flex', justifyContent: 'center' }}>
              <Progress
                type="circle"
                percent={percent}
                size={42}
                strokeWidth={8}
                strokeColor={strokeColor}
                trailColor="#eef0f3"
                format={() => (
                  <span style={{ fontSize: 10.5, fontWeight: 600, color: strokeColor }}>{percent}%</span>
                )}
              />
            </div>
          </Tooltip>
        );
      },
    },
    {
      title: <RunResultHeader />,
      key: 'runResult',
      width: columnWidths.runResult,
      align: 'center',
      onHeaderCell: resizeHeaderCell('runResult'),
      render: (_, record) => <RunResultCell record={record} />,
    },
    {
      title: '状态',
      dataIndex: 'statusName',
      key: 'statusName',
      width: columnWidths.statusName,
      align: 'center',
      onHeaderCell: resizeHeaderCell('statusName'),
      render: (_, record) => (
        <JobStatusTag status={record.status} statusName={record.statusName} />
      ),
    },
    {
      title: '操作',
      key: 'action',
      width: columnWidths.action,
      align: 'center',
      onHeaderCell: resizeHeaderCell('action'),
      render: (_, record) => (
        <Space>
          <Tooltip title="复制该 Job 的发起参数进入创建页面：默认按包运行；编译任务自动带上编译后的包，切到编译模式则回填原编译选项。">
            <Button size="small" icon={<CopyOutlined />} onClick={() => handleCopyJob(record)}>
              复制
            </Button>
          </Tooltip>
          <Tooltip title="“取消”会停止 Job，然后清理掉 Job 现场、日志。">
            <Popconfirm
              title="确认取消"
              description="确定要取消该 Job 吗？取消后 Job 将停止并清理 Job 现场、日志。"
              onConfirm={() => handleDelete(record.jobId)}
              okText="确定"
              cancelText="取消"
            >
              <Button size="small" danger>
                取消
              </Button>
            </Popconfirm>
          </Tooltip>
          <Popover
            overlayClassName="job-action-popover"
            content={
              <div className="job-action-menu">
                {record.isolated ? (
                  <Popconfirm
                    title="确认解除隔离"
                    description="确定要解除该 Job 的隔离状态吗？恢复调度平台的正常处理。"
                    onConfirm={() => handleUnisolate(record.jobId)}
                    okText="确定"
                    cancelText="取消"
                  >
                    <div className="job-action-menu__item job-action-menu__item--success">
                      <UnlockOutlined className="job-action-menu__icon" />
                      <span className="job-action-menu__label">解除隔离</span>
                    </div>
                  </Popconfirm>
                ) : (
                  <Popconfirm
                    title="确认隔离"
                    description="确定要隔离该 Job 吗？隔离后 Job 现场与日志将被保留，可通过“解除隔离”恢复。"
                    onConfirm={() => handleIsolate(record.jobId)}
                    okText="确定"
                    cancelText="取消"
                  >
                    <div className="job-action-menu__item job-action-menu__item--danger">
                      <StopOutlined className="job-action-menu__icon" />
                      <span className="job-action-menu__label">隔离</span>
                    </div>
                  </Popconfirm>
                )}

                <div
                  className="job-action-menu__item"
                  onClick={() => handleViewLog(record.jobId)}
                >
                  <FileTextOutlined className="job-action-menu__icon" />
                  <span className="job-action-menu__label">运行日志</span>
                  <RightOutlined className="job-action-menu__chevron" />
                </div>

                {record.buildType === 'compile_platform' && (
                  <div
                    className="job-action-menu__item"
                    onClick={() => handleViewBuildLog(record.jobId)}
                  >
                    <CodeOutlined className="job-action-menu__icon" />
                    <span className="job-action-menu__label">编译日志</span>
                    <RightOutlined className="job-action-menu__chevron" />
                  </div>
                )}

                {record.buildType === 'package_bazel' && (
                  <>
                    <div
                      className="job-action-menu__item"
                      onClick={() => handleViewBazelBuildLog(record.jobId)}
                    >
                      <CodeOutlined className="job-action-menu__icon" />
                      <span className="job-action-menu__label">编译日志</span>
                      <RightOutlined className="job-action-menu__chevron" />
                    </div>
                    <div
                      className="job-action-menu__item"
                      onClick={() => handleViewArgoWorkflow(record.jobId)}
                    >
                      <FileTextOutlined className="job-action-menu__icon" />
                      <span className="job-action-menu__label">Argo Workflow</span>
                      <RightOutlined className="job-action-menu__chevron" />
                    </div>
                  </>
                )}

                <div className="job-action-menu__divider" />

                <div className="job-action-menu__meta">
                  <div className="job-action-menu__meta-label">调度任务 ID</div>
                  <div className="job-action-menu__meta-value">
                    <span className="job-action-menu__meta-id" title={record.dispatchJobId || ''}>
                      {record.dispatchJobId || '—'}
                    </span>
                    {record.dispatchJobId && (
                      <Tooltip title="复制调度任务 ID">
                        <span
                          className="job-action-menu__copy"
                          onClick={() => {
                            navigator.clipboard
                              ?.writeText(record.dispatchJobId as string)
                              .then(() => message.success('调度任务 ID 已复制'))
                              .catch(() => message.error('复制失败'));
                          }}
                        >
                          <CopyOutlined />
                        </span>
                      </Tooltip>
                    )}
                  </div>
                </div>
              </div>
            }
            trigger="click"
          >
            <Button size="small" icon={<MoreOutlined />} />
          </Popover>
        </Space>
      ),
    },
  ];

  return (
    <div className="layout-container">
      {/* 筛选栏 */}
      <div className="layout-filter">
        {/* onFinish + 查询按钮 htmlType="submit":输入框内按 Enter 即查询。详见 JobList 同处注释。 */}
        <Form form={form} layout="inline" onFinish={handleSearch}>
          <Form.Item name="job_id" label="JobID">
            <Input placeholder="请输入JobID" allowClear style={{ width: 200 }} />
          </Form.Item>
          <Form.Item name="job_name" label="Job 名称">
            <Input placeholder="请输入 Job 名称" allowClear style={{ width: 200 }} />
          </Form.Item>
          {/* <Form.Item name="start_time" label="发起时间">
            <RangePicker
              showTime
              format="YYYY-MM-DD HH:mm:ss"
              style={{ width: 400 }}
            />
          </Form.Item> */}
          {/*
            冷热分离 v2 一期:时间选择器改成"chip + Popover"折叠式设计。
            默认收起,chip 上一行文字自解释当前时间窗("最近 30 天"/日期区间);
            点击 chip 展开 Popover,内嵌快捷预设按钮 + RangePicker。
            与主 JobList 同款,让后端触发 PG 分区 pruning。
          */}
          <Form.Item name="partition_time_range" label="创建时间" style={{ marginBottom: 0 }}>
            <TimeRangeChip />
          </Form.Item>
          <Form.Item name="priority" label="优先级">
            <Select
              {...enterToSearch(1)}
              placeholder="请选择优先级"
              allowClear
              style={{ width: 150 }}
            >
              <Select.Option value="">全部</Select.Option>
              {priorityList.map((item, index) => (
                <Select.Option key={item.code ?? item.value ?? index} value={item.value}>
                  {item.label}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
          <Form.Item name="task_status" label="Job 状态">
            <Select
              {...enterToSearch(2)}
              placeholder="请选择 Job 状态"
              allowClear
              style={{ width: 150 }}
            >
              {statusList.map((item, index) => (
                <Select.Option key={item.code ?? item.value ?? index} value={item.value}>
                  {item.label}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
          <Form.Item name="source" label="数据源">
            <Select
              {...enterToSearch(3)}
              placeholder="请选择数据源"
              allowClear
              style={{ width: 150 }}
            >
              <Select.Option value="">全部</Select.Option>
              {sourceList.map((item, index) => (
                <Select.Option key={item.code ?? item.value ?? index} value={item.value}>
                  {item.label}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
          <Form.Item name="creator" label="创建人">
            <Input placeholder="请输入创建人" allowClear style={{ width: 150 }} />
          </Form.Item>
          <Form.Item>
            <Checkbox
              checked={onlyMine}
              onChange={e => handleToggleOnlyMine(e.target.checked)}
            >
              只看自己
            </Checkbox>
          </Form.Item>
          <Form.Item>
            <Space>
              <Button type="primary" htmlType="submit">
                查询
              </Button>
              <Button onClick={handleReset}>重置</Button>
              <Button type="primary" onClick={handleCreateTask}>
                创建 Job
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </div>

      {/* 任务列表 */}
      <div className="layout-content">
        <Table
          rowKey="jobId"
          dataSource={data}
          columns={columns}
          loading={loading}
          size="small"
          components={{ header: { cell: ResizableTitle } }}
          tableLayout="fixed"
          scroll={{ x: Object.values(columnWidths).reduce((a, b) => a + b, 0) }}
          pagination={{
            current: queryParams.page,
            pageSize: queryParams.per_page,
            total: total,
            showSizeChanger: true,
            pageSizeOptions: ['10', '20', '50', '100', '1000'],
            showQuickJumper: true,
            showTotal: (t) => `总共 ${t} 条`,
            onChange: (page, pageSize) => {
              setQueryParams({ ...queryParams, page, per_page: pageSize });
            },
          }}
        />
      </div>

      {/* 日志查看 Modal（运行日志 / 编译日志共用，按 logKind 区分标题与空态文案）*/}
      <Modal
        title={`${logKind === 'build' ? '编译日志' : 'Job 日志'} - Job ID: ${currentJobId}`}
        open={logModalVisible}
        onCancel={() => {
          setLogModalVisible(false);
          setLogContent('');
          setLogDownloadUrl(null);
          setCurrentJobId(null);
        }}
        footer={[
          <Button key="close" onClick={() => {
            setLogModalVisible(false);
            setLogContent('');
            setLogDownloadUrl(null);
            setCurrentJobId(null);
          }}>
            关闭
          </Button>,
        ]}
        width={800}
        style={{ top: 20 }}
      >
        {logKind === 'build' && logDownloadUrl && (
          <div style={{ marginBottom: 12 }}>
            <a
              href={logDownloadUrl}
              target="_blank"
              rel="noopener noreferrer"
              style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}
            >
              <DownloadOutlined />
              查看 / 下载完整编译日志
            </a>
          </div>
        )}
        <Spin spinning={logLoading}>
          <div
            style={{
              maxHeight: '70vh',
              overflow: 'auto',
              backgroundColor: '#18181b',
              color: '#a1a1aa',
              padding: '16px',
              fontFamily: "'JetBrains Mono', Consolas, Monaco, monospace",
              fontSize: '12px',
              lineHeight: '1.6',
              whiteSpace: 'pre-wrap',
              wordBreak: 'break-word',
              borderRadius: 6,
              border: '1px solid #27272a',
            }}
          >
            {logContent || (logLoading ? '加载中...' : (logKind === 'build' ? '暂无编译日志' : '暂无日志'))}
          </div>
        </Spin>
      </Modal>

      {/* 场景质检 · 创建 Job 弹窗（独立组件，任务类型固定 SCENE_QC，不复用 CreateJobModal） */}
      <CreateSceneQcJobModal
        open={createModalVisible}
        copyRecord={copyRecord}
        onClose={() => {
          setCreateModalVisible(false);
          setCopyRecord(null);
        }}
        onSuccess={loadJobList}
      />

      {versionTraceModal}
    </div>
  );
}
